import { REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY } from '@features/account-registration/constants';
import {
  ERROR_CODE_TO_ALERT_MAPPING,
  ErrorAlertMapping,
} from '@features/account-registration/errors';

/**
 * Selectors
 */
export const MANAGER_ACCOUNT_CREATION_PAGE_ID = 'manager-account-creation';

/**
 * Translation keys
 */
export const MANAGER_ACCOUNT_CREATION_TITLE_TRANSLATION_KEY =
  'manager_account_creation_title';
export const MANAGER_ACCOUNT_CREATION_DETAILS_HEADING_TRANSLATION_KEY =
  'manager_account_creation_details_heading';
export const MANAGER_ACCOUNT_CREATION_BUSINESS_SECTION_DESCRIPTION_TRANSLATION_KEY =
  'manager_account_creation_business_section_description';
export const MANAGER_ACCOUNT_CREATION_SPINNER_TRANSLATION_KEY =
  'manager_account_creation_spinner_text';
export const MANAGER_ACCOUNT_CREATION_ERROR_TRANSLATION_KEY =
  'manager_account_creation_error_alert';
export const MANAGER_ACCOUNT_CREATION_REFUSED_TRANSLATION_KEY =
  'manager_account_creation_refused_alert';
export const MANAGER_ACCOUNT_CREATION_LIMIT_REACHED_TRANSLATION_KEY =
  'manager_account_creation_limit_reached_alert';
export const MANAGER_ACCOUNT_CREATION_CREATED_NO_NAVIGATION_TRANSLATION_KEY =
  'manager_account_creation_created_no_navigation_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_ADDRESS_TRANSLATION_KEY =
  'manager_account_creation_invalid_address_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_INPUT_TRANSLATION_KEY =
  'manager_account_creation_invalid_input_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_ADDRESS_LINE1_TRANSLATION_KEY =
  'manager_account_creation_invalid_address_line1_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_CITY_TRANSLATION_KEY =
  'manager_account_creation_invalid_city_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_COUNTRY_TRANSLATION_KEY =
  'manager_account_creation_invalid_country_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_PHONE_TRANSLATION_KEY =
  'manager_account_creation_invalid_phone_alert';
export const MANAGER_ACCOUNT_CREATION_INVALID_BUSINESS_NAME_TRANSLATION_KEY =
  'manager_account_creation_invalid_business_name_alert';
export const MANAGER_ACCOUNT_CREATION_TIME_ZONE_PLACEHOLDER_TRANSLATION_KEY =
  'manager_account_creation_time_zone_placeholder';
export const MANAGER_ACCOUNT_CREATION_INDUSTRY_PLACEHOLDER_TRANSLATION_KEY =
  'manager_account_creation_industry_placeholder';
export const MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_ERROR_TRANSLATION_KEY =
  'manager_account_creation_country_load_error';
export const MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_RETRY_TRANSLATION_KEY =
  'manager_account_creation_country_load_retry';
export const MANAGER_ACCOUNT_CREATION_STATE_LOAD_ERROR_TRANSLATION_KEY =
  'manager_account_creation_state_load_error';
export const MANAGER_ACCOUNT_CREATION_ENUMERATIONS_LOAD_ERROR_TRANSLATION_KEY =
  'manager_account_creation_enumerations_load_error';

/**
 * Error codes
 */
// GRS codes whose sponsored ads copy names the advertiser; this page
// re-words them
export const INVALID_ADDRESS_ERROR_CODE = 'INVALID_ADDRESS';
export const INVALID_INPUT_ERROR_CODE = 'INVALID_INPUT';
// GRS codes whose sponsored ads copy already fits a refused address field
export const INVALID_ZIP_CODE_ERROR_CODE = 'INVALID_ZIP_CODE';
export const INVALID_STATE_OR_REGION_ERROR_CODE = 'INVALID_STATE_OR_REGION';
// BFF request-level 400 code when the creating user holds the maximum
// number of manager accounts GRS allows
export const MANAGER_ACCOUNT_LIMIT_REACHED_ERROR_CODE =
  'MANAGER_ACCOUNT_LIMIT_REACHED';
// Not service codes: the page's own entries for an INVALID_INPUT refusal
// whose fieldLocation names an address field with no code of its own
export const INVALID_ADDRESS_LINE1_FIELD_CODE = 'INVALID_ADDRESS_LINE1';
export const INVALID_CITY_FIELD_CODE = 'INVALID_CITY';
export const INVALID_COUNTRY_FIELD_CODE = 'INVALID_COUNTRY';
export const INVALID_PHONE_FIELD_CODE = 'INVALID_PHONE';
export const INVALID_BUSINESS_NAME_FIELD_CODE = 'INVALID_BUSINESS_NAME';
// Not service codes: the page's own fallbacks for a refused item whose code
// has no entry, and for a failed request whose code has none
export const MANAGER_ACCOUNT_REFUSED_FALLBACK_CODE = 'MANAGER_ACCOUNT_REFUSED';
export const MANAGER_ACCOUNT_CREATION_FAILED_FALLBACK_CODE =
  'MANAGER_ACCOUNT_CREATION_FAILED';

/**
 * Request paths GRS puts in fieldLocation when Address Service refuses a
 * field of the business address
 */
export const ADDRESS_FIELD_LOCATION_ZIP_CODE =
  'businessDetails.address.zipCode';
export const ADDRESS_FIELD_LOCATION_STATE = 'businessDetails.address.state';
export const ADDRESS_FIELD_LOCATION_ADDRESS_LINE1 =
  'businessDetails.address.addressLine1';
export const ADDRESS_FIELD_LOCATION_CITY = 'businessDetails.address.city';
export const ADDRESS_FIELD_LOCATION_COUNTRY_CODE =
  'businessDetails.address.countryCode';
export const ADDRESS_FIELD_LOCATION_PHONE_NUMBER =
  'businessDetails.address.phoneNumber';
export const ADDRESS_FIELD_LOCATION_BUSINESS_NAME =
  'businessDetails.address.businessName';

/**
 * GRS reports an Address Service rejection as INVALID_INPUT with the
 * request path of the field it refused. The path picks the copy; the zip
 * code and state reuse the sponsored ads codes that already carry theirs.
 */
export const INVALID_INPUT_FIELD_LOCATION_TO_CODE: Record<string, string> = {
  [ADDRESS_FIELD_LOCATION_ZIP_CODE]: INVALID_ZIP_CODE_ERROR_CODE,
  [ADDRESS_FIELD_LOCATION_STATE]: INVALID_STATE_OR_REGION_ERROR_CODE,
  [ADDRESS_FIELD_LOCATION_ADDRESS_LINE1]: INVALID_ADDRESS_LINE1_FIELD_CODE,
  [ADDRESS_FIELD_LOCATION_CITY]: INVALID_CITY_FIELD_CODE,
  [ADDRESS_FIELD_LOCATION_COUNTRY_CODE]: INVALID_COUNTRY_FIELD_CODE,
  [ADDRESS_FIELD_LOCATION_PHONE_NUMBER]: INVALID_PHONE_FIELD_CODE,
  [ADDRESS_FIELD_LOCATION_BUSINESS_NAME]: INVALID_BUSINESS_NAME_FIELD_CODE,
};

/**
 * Alert copy per error code for this page. The sponsored ads registration
 * mapping is the base; the entries whose copy there names the advertiser are
 * replaced with manager-account-neutral wording, and the page's own codes
 * are added.
 */
export const MANAGER_ACCOUNT_ERROR_ALERT_MAPPING: ErrorAlertMapping = {
  ...ERROR_CODE_TO_ALERT_MAPPING,
  [INVALID_ADDRESS_ERROR_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_ADDRESS_TRANSLATION_KEY,
  },
  [INVALID_INPUT_ERROR_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_INPUT_TRANSLATION_KEY,
  },
  [INVALID_ADDRESS_LINE1_FIELD_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_ADDRESS_LINE1_TRANSLATION_KEY,
  },
  [INVALID_CITY_FIELD_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_CITY_TRANSLATION_KEY,
  },
  [INVALID_COUNTRY_FIELD_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_COUNTRY_TRANSLATION_KEY,
  },
  [INVALID_PHONE_FIELD_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_PHONE_TRANSLATION_KEY,
  },
  [INVALID_BUSINESS_NAME_FIELD_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_INVALID_BUSINESS_NAME_TRANSLATION_KEY,
  },
  [MANAGER_ACCOUNT_LIMIT_REACHED_ERROR_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_LIMIT_REACHED_TRANSLATION_KEY,
  },
  [MANAGER_ACCOUNT_REFUSED_FALLBACK_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_REFUSED_TRANSLATION_KEY,
  },
  [MANAGER_ACCOUNT_CREATION_FAILED_FALLBACK_CODE]: {
    header: REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: MANAGER_ACCOUNT_CREATION_ERROR_TRANSLATION_KEY,
  },
};
